import { useStaticQuery, graphql } from "gatsby";
import { useMemo } from "react";

export type Title = {
  slug: string;
  name: string;
  icon: {
    file: {
      contentType: string;
      url: string;
    };
  };
  bettingMetaTitle?: string;
  bettingMetaDescription?: string;
  pickemsMetaTitle?: string;
  pickemsMetaDescription?: string;
  logo?: any;
  hero?: any;
};

export type Titles = {
  [slug: string]: Title;
};

export const useTitles = (): Titles => {
  const { allContentfulTitle } = useStaticQuery<{
    allContentfulTitle: { nodes: Queries.ContentfulTitle[] };
  }>(graphql`
    {
      allContentfulTitle {
        nodes {
          name
          slug
          icon {
            ...IconImage
          }
          bettingMetaTitle
          bettingMetaDescription
          pickemsMetaTitle
          pickemsMetaDescription
          hero {
            ...HeroImage
          }
          logo {
            ...IconImage
          }
        }
      }
    }
  `);

  return useMemo(
    () =>
      allContentfulTitle.nodes
        .filter(({ slug }) => slug && slug !== "empty")
        .reduce<Titles>((allTitles, title) => {
          if (title.slug) {
            allTitles[title.slug] = {
              slug: title.slug,
              name: title.name || "",
              icon: {
                file: {
                  contentType: title.icon?.file?.contentType || "",
                  url: title.icon?.file?.url || "",
                },
              },
              bettingMetaTitle: title.bettingMetaTitle || undefined,
              bettingMetaDescription: title.bettingMetaDescription || undefined,
              pickemsMetaTitle: title.pickemsMetaTitle || undefined,
              pickemsMetaDescription: title.pickemsMetaDescription || undefined,
              hero: title.hero,
              logo: title.logo,
            };
          }
          return allTitles;
        }, {} as Titles),
    [allContentfulTitle],
  );
};

export const useTitle = (title: string): Title => {
  const titles = useTitles();

  const titleSlug =
    Object.keys(titles).find((slug) => slug === title?.toLowerCase()) ?? "";

  return titles[titleSlug];
};
